/* Card image slider — Whimsy Flowers (Craft theme)
   - Swipe (mobile) / dots (all devices)
   - Auto slide: only while the card is on screen, pauses on hover/touch
   - Custom element, so it also works after filtering/sorting re-renders the grid */
if (!customElements.get('card-slider')) {
  customElements.define(
    'card-slider',
    class CardSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.cis__track');
        if (!this.track || this.track.children.length < 2) return;

        this.dots = Array.from(this.querySelectorAll('.cis__dot'));
        this.delay = parseInt(this.dataset.autoplay, 10) || 0; // ms, 0 = off
        this.paused = false;
        this.visible = false;

        // Dot clicks (capture phase so the product link doesn't grab it)
        this.addEventListener('click', (e) => {
          const dot = e.target.closest('.cis__dot');
          if (!dot || !this.contains(dot)) return;
          e.preventDefault();
          e.stopPropagation();
          this.goTo(this.dots.indexOf(dot));
          this.restart();
        }, true);

        // Keep dots in sync when swiping
        this.track.addEventListener('scroll', () => {
          cancelAnimationFrame(this.raf);
          this.raf = requestAnimationFrame(() => this.setActive(this.index));
        }, { passive: true });

        if (!this.delay) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        // Pause while the customer is interacting
        this.addEventListener('mouseenter', () => { this.paused = true; this.stop(); });
        this.addEventListener('mouseleave', () => { this.paused = false; this.restart(); });
        this.track.addEventListener('touchstart', () => { this.paused = true; this.stop(); }, { passive: true });
        this.track.addEventListener('touchend', () => {
          this.paused = false;
          this.restart(this.delay * 2); // give them a moment after swiping
        }, { passive: true });

        // Only run while the card is on screen
        this.io = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.visible ? this.restart(this.delay + Math.random() * 1200) : this.stop();
        }, { threshold: 0.5 });
        this.io.observe(this);

        // Stop when the browser tab is hidden
        this.onVis = () => (document.hidden ? this.stop() : this.restart());
        document.addEventListener('visibilitychange', this.onVis);
      }

      disconnectedCallback() {
        this.stop();
        this.io?.disconnect();
        if (this.onVis) document.removeEventListener('visibilitychange', this.onVis);
      }

      get index() {
        const w = this.track.clientWidth || 1;
        return Math.round(this.track.scrollLeft / w);
      }

      goTo(i) {
        const n = this.track.children.length;
        const target = ((i % n) + n) % n; // loops back to the first image
        this.setActive(target);
        this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
      }

      setActive(i) {
        this.dots.forEach((d, j) => {
          d.classList.toggle('is-active', j === i);
          d.setAttribute('aria-current', j === i ? 'true' : 'false');
        });
      }

      stop() {
        clearTimeout(this.timer);
        this.timer = null;
      }

      restart(wait = this.delay) {
        this.stop();
        if (!this.delay || this.paused || !this.visible || document.hidden) return;
        this.timer = setTimeout(() => {
          this.goTo(this.index + 1);
          this.restart();
        }, wait);
      }
    }
  );
}